/**
 * `runConnectPhase` — the actual WebRTC connect sequence, step by step (the "1/7…7/7"
 * lines in the diagnostics log are this file's table of contents). Used for BOTH the
 * first connect and every rejoin (the controller's `rejoin()` in `operator-session.ts`
 * is just "tear down, then run this phase again") — intentionally one function for
 * both: joining a mediasoup room is the same protocol exchange every time, as long as
 * any previous transports were fully torn down first.
 *
 * Isolated on purpose: this function returns its result (or throws) and only calls the
 * collaborators handed to it via `ctx`. It never reaches into a shared session object,
 * never imports `recovery-phase.ts` or `teardown-phase.ts`, and has no idea the
 * controller might call it again later — the controller is the only thing that knows
 * this phase exists and decides when to (re)run it.
 *
 * The steps, for a reader new to WebRTC/mediasoup:
 *   1. Load the mediasoup-client library (see `mediasoup-loader.ts`).
 *   2. Ask the server what it's capable of (`getRouterRtpCapabilities`) and build a
 *      local `Device` that understands those capabilities — this negotiation replaces
 *      the SDP offer/answer dance a "plain" WebRTC app would do.
 *   3. Create a SEND transport (this browser → server) and a RECV transport (server →
 *      this browser) — mediasoup always uses two independent transports, each with its
 *      own ICE/DTLS handshake, instead of one bidirectional connection.
 *   4. Actually join the room (`join` request) — the server starts pushing consumers for
 *      whatever is already being produced (see `room-consumers.ts`).
 *   5. Open the operator DataChannels (PROTOCOL.md §2): `chat` — reliable + ordered, for
 *      commands (see `chat-channel.ts`, drains anything queued while it was still
 *      closed); `control` — unordered, no retransmits, for the operator-input stream
 *      (see `control-channel.ts`).
 *   6. Wait for the send transport to finish connecting — only then is the operator
 *      control path actually usable.
 */

import type { types as Mediasoup } from "mediasoup-client";

import { loadMediasoupClient } from "./mediasoup-loader";
import { wireTransportDiag, waitForTransportConnected } from "./transport-diagnostics";
import { emitStatus, type EmitStatusOpts } from "./session-status";
import { SessionPhase, STATUS_CODES } from "../status-codes";
import type { ProtooClient } from "./protoo-client";
import type { ChatChannel } from "./chat-channel";
import type { ControlChannel } from "./control-channel";
import type { VideoProducerWatchdog } from "./video-producer-watchdog";
import type { ConnectionQualityMonitor } from "../../connection-stats";
import type { PhaseTransports } from "./teardown-phase";
import type { RtcStatsEntry } from "../../connection-stats";

/** The subset of `cfg` this phase reads — see `operator-session.ts` for the full shape. */
export interface ConnectPhaseConfig {
  iceServers?: RTCIceServer[];
  iceTransportPolicy?: RTCIceTransportPolicy;
  receiveVideo?: boolean;
}

/** The subset of the video pipeline this phase reads. */
export interface ConnectPhasePipeline {
  countVideoTracks: () => number;
  onRecvTransportReady?: () => void;
  getAggregateConsumerStats: () => Promise<Map<string, RtcStatsEntry>>;
}

export interface ConnectPhaseContext {
  cfg: ConnectPhaseConfig;
  protoo: ProtooClient;
  pipeline: ConnectPhasePipeline;
  sig: (msg: string) => void;
  opts: EmitStatusOpts;
  connectionQuality: ConnectionQualityMonitor;
  recvTransportRef: { current: Mediasoup.Transport | null };
  chat: ChatChannel;
  control: ControlChannel;
  videoWatchdog: VideoProducerWatchdog;
  onTransportFailed: (role: string) => void;
  startCapabilitiesRequest: () => void;
}

export interface ConnectPhaseResult extends PhaseTransports {
  device: Mediasoup.Device;
}

function toError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}

interface WebRtcTransportInfo {
  transportId?: string;
  id?: string;
  iceParameters: Mediasoup.IceParameters;
  iceCandidates: Mediasoup.IceCandidate[];
  dtlsParameters: Mediasoup.DtlsParameters;
  sctpParameters?: Mediasoup.SctpParameters;
}

/**
 * The boilerplate shared by the send and recv transport, extracted so the two call
 * sites below only need to state what's actually DIFFERENT between them (appData
 * direction, the recv-only ready hook, the send-only `producedata` handler): ask the
 * server for a transport, create the local mediasoup-client side of it, wire connection
 * diagnostics, and answer its `"connect"` event by relaying DTLS parameters back to the
 * server.
 */
async function createAndWireTransport(
  ctx: ConnectPhaseContext,
  device: Mediasoup.Device,
  role: "send" | "recv",
  step: string,
  onReady: (() => void) | null = null
): Promise<Mediasoup.Transport> {
  const { cfg, protoo, sig, onTransportFailed } = ctx;

  sig(`${step} createWebRtcTransport (${role})…`);
  const info: WebRtcTransportInfo = await protoo.request("createWebRtcTransport", {
    forceTcp: false,
    sctpCapabilities: device.sctpCapabilities,
    appData: { direction: role === "send" ? "producer" : "consumer" },
  });

  // Server always returns at least one of transportId/id — same trust boundary as
  // before typing this; a missing id fails downstream in create*Transport exactly as
  // it would have unvalidated.
  const transportOptions = {
    id: (info.transportId || info.id)!,
    iceParameters: info.iceParameters,
    iceCandidates: info.iceCandidates,
    dtlsParameters: info.dtlsParameters,
    sctpParameters: info.sctpParameters,
    iceServers: cfg.iceServers || [],
    iceTransportPolicy: cfg.iceTransportPolicy || "all",
  };
  const transport =
    role === "send"
      ? device.createSendTransport(transportOptions)
      : device.createRecvTransport(transportOptions);
  wireTransportDiag(transport, role, sig, onTransportFailed, onReady);
  sig(`${step} ${role} transport id=${transport.id}`);

  transport.on("connect", ({ dtlsParameters }, cb, err) => {
    void (async () => {
      try {
        await protoo.request("connectWebRtcTransport", {
          transportId: transport.id,
          dtlsParameters,
        });
        sig(`${step} ${role} transport DTLS connected id=${transport.id}`);
        cb();
      } catch (e) {
        err(toError(e));
      }
    })();
  });

  return transport;
}

export async function runConnectPhase(ctx: ConnectPhaseContext): Promise<ConnectPhaseResult> {
  const { protoo, pipeline, sig, opts, recvTransportRef } = ctx;

  const mediasoupClient = await loadMediasoupClient(sig);

  const capsData = await protoo.request("getRouterRtpCapabilities", {});
  const caps: Mediasoup.RtpCapabilities =
    "routerRtpCapabilities" in capsData ? capsData.routerRtpCapabilities : capsData;
  const Device = mediasoupClient.Device ?? mediasoupClient.default?.Device;
  if (!Device) {
    throw new Error("mediasoup-client: нет экспорта Device");
  }
  const device = new Device();
  await device.load({ routerRtpCapabilities: caps });
  sig("3/7 getRouterRtpCapabilities + Device loaded");
  sig(`Video receive codecs: ${JSON.stringify((device.rtpCapabilities.codecs ?? [])
    .filter((codec) => codec.kind === "video")
    .map((codec) => ({ mimeType: codec.mimeType, parameters: codec.parameters })))}`);

  const sendTransport = await createAndWireTransport(ctx, device, "send", "4/7");

  sendTransport.on(
    "producedata",
    ({ sctpStreamParameters, label, protocol, appData }, callback, errback) => {
      void (async () => {
        try {
          const res = await protoo.request("produceData", {
            transportId: sendTransport.id,
            sctpStreamParameters,
            label: label ?? "",
            protocol: protocol ?? "",
            appData,
          });
          const id = res.id ?? res.dataProducerId;
          if (!id) throw new Error(`produceData: no id`);
          callback({ id: String(id) });
        } catch (e) {
          errback(toError(e));
        }
      })();
    }
  );

  const recvTransport = await createAndWireTransport(ctx, device, "recv", "5/7", () => {
    pipeline?.onRecvTransportReady?.();
  });
  recvTransportRef.current = recvTransport;
  ctx.connectionQuality.startConnectionQualityMonitor(recvTransport, () =>
    pipeline.getAggregateConsumerStats()
  );

  sig("6/7 join room…");
  const receiveVideo = ctx.cfg.receiveVideo !== false;
  await protoo.request("join", {
    displayName: receiveVideo ? "Operator UI" : "Quest XR input",
    device: { flag: "browser", name: "operator-ui", version: __APP_VERSION__ },
    rtpCapabilities: receiveVideo ? device.rtpCapabilities : {
      ...device.rtpCapabilities,
      codecs: (device.rtpCapabilities.codecs ?? []).filter((codec) => codec.kind !== "video"),
    },
    sctpCapabilities: device.sctpCapabilities,
    consume: true,
  });
  sig("6/7 join room OK");

  if (receiveVideo && pipeline.countVideoTracks() === 0) {
    emitStatus(opts, STATUS_CODES.IN_ROOM_WAITING_VIDEO, SessionPhase.JOINED);
    ctx.videoWatchdog.scheduleCheck();
  }

  try {
    sig("7/7 produceData(chat)…");
    // Commands (stop, source select, camera, capabilities) must arrive, and in order —
    // a stop overtaking the last input frame is exactly what this channel must never do.
    const chatDataProducer = await sendTransport.produceData({
      ordered: true,
      label: "chat",
      protocol: "",
      // The SFU fans a data producer out to the other peers by appData.channel === "chat".
      appData: { channel: "chat" },
    });
    sig(`7/7 operator chat DataProducer id=${chatDataProducer.id}`);
    ctx.chat.attach(chatDataProducer, {
      onOpen: () => {
        sig("7/7 operator chat DataChannel open");
        ctx.startCapabilitiesRequest();
      },
    });
  } catch (err) {
    sig(`7/7 produceData(chat) failed: ${err}`);
    emitStatus(opts, STATUS_CODES.COMMAND_CHANNEL_UNAVAILABLE);
  }

  try {
    // operator-input stream: a late frame is worse than a lost one — no ordering, no
    // retransmits; the backend drops anything older than the last seq it accepted.
    const controlDataProducer = await sendTransport.produceData({
      ordered: false,
      maxRetransmits: 0,
      label: "control",
      protocol: "",
      appData: { channel: "chat" },
    });
    sig(`7/7 operator control DataProducer id=${controlDataProducer.id}`);
    ctx.control.attach(controlDataProducer);
  } catch (err) {
    sig(`7/7 produceData(control) failed: ${err}`);
    emitStatus(opts, STATUS_CODES.COMMAND_CHANNEL_UNAVAILABLE);
  }

  // Unconditional (not just on the produceData path above): the operator control path
  // is dead either way if the send transport itself never connects, not just chat —
  // so a stuck ICE/DTLS handshake here is a hard join failure, not the softer
  // COMMAND_CHANNEL_UNAVAILABLE case above.
  const timeout = 5_000;
  sig(`waiting send transport connect (≤${timeout / 1000}s)…`);
  await waitForTransportConnected(sendTransport, "send", timeout);
  sig("send transport connected");

  return { device, sendTransport, recvTransport };
}
